import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SocketService } from '@frontend/common/util';
import { merge, Observable, of, race, throwError } from 'rxjs';
import { catchError, filter, first, ignoreElements, mergeMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Tag } from '../tag/tag.model';

@Injectable({
  providedIn: 'root',
})
export class TagApiService {
  constructor(private http: HttpClient, private socketSrv: SocketService) {}

  public bulk(body: { items: Tag[]; correlation_id: string }) {
    return this.http.post('tags/bulk', body);
  }

  public create(entity: Tag) {
    const correlation_id = entity.id;
    return merge(
      this.http.post('tags', { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError(() => ({ id: entity.id, message })))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('tags.create')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('tags.create').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError(() => ({ id: entity.id, message })))
        )
      ).pipe(first())
    );
  }

  public delete(id) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.request('delete', `tags/${id}`, { body: { correlation_id } }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(mergeMap((message) => throwError(() => ({ id, message }))))
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('tags.remove')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('tags.remove').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError(() => ({ id, message })))
        )
      ).pipe(first())
    );
  }

  public get(params?: { [key: string]: string | string[] }): Observable<Tag[]> {
    return this.http.get<Tag[]>('tags', { params });
  }

  public getOne(id) {
    return this.http.get<Tag>(`tags/${id}`);
  }

  public update(entity) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.put(`tags/${entity.id}`, { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError(() => ({ id: entity.id, message })))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('tags.update')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('tags.update').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError(() => ({ id: entity.id, message })))
        )
      ).pipe(first())
    );
  }
}
